import { Component, input } from '@angular/core';

/**
 * "1/3" with a small pie beside it that fills while the page is shown and starts again on the next
 * one, so the viewer can tell how soon the page turns. The pie is an SVG circle whose stroke is as
 * wide as the circle: animating its dash offset fills it like a cake. It is re-created on every
 * page (`track index`), which restarts the animation in step with the turn. `elapsed` starts it
 * part-way, for a page already some seconds on the screen.
 */
@Component({
  selector: 'app-page-pie',
  template: `
    @for (page of [index()]; track page) {
      <svg class="pie" viewBox="0 0 32 32" aria-hidden="true">
        <circle class="track" cx="16" cy="16" r="15" />
        <circle
          class="fill"
          cx="16"
          cy="16"
          r="7.5"
          [style.animation-duration.s]="seconds()"
          [style.animation-delay.s]="-elapsed()"
          [style.animation-timing-function]="'steps(' + seconds() + ')'"
        />
      </svg>
    }
    <span class="label">{{ index() + 1 }}/{{ count() }}</span>
  `,
  styles: `
    :host {
      display: inline-flex;
      align-items: center;
      gap: 0.4em;
      font-size: var(--fontsize-small);
      font-weight: 700;
      line-height: 1;
    }
    .pie {
      width: 1.2em;
      height: 1.2em;
      transform: rotate(-90deg); /* start at twelve o'clock */
    }
    .track {
      fill: rgba(var(--ink-rgb, 255, 255, 255), 0.2);
      stroke: rgba(var(--ink-rgb, 255, 255, 255), 0.85);
      stroke-width: 2;
    }
    /* Circumference of r = 7.5 is 2π·7.5 ≈ 47.12; a stroke 15 wide covers the whole disc. */
    .fill {
      fill: none;
      stroke: rgb(var(--ink-rgb, 255, 255, 255));
      stroke-width: 15;
      stroke-dasharray: 47.12;
      stroke-dashoffset: 47.12;
      animation-name: fill-pie;
      /* A step a second (the timing function is set from the seconds): the pie says how soon, and a
         smooth fill would repaint every frame on the wall's weak hardware. */
      animation-fill-mode: forwards;
    }
    @keyframes fill-pie {
      to { stroke-dashoffset: 0; }
    }
  `,
})
export class PagePieComponent {
  /** Zero-based page shown now. */
  readonly index = input.required<number>();
  readonly count = input.required<number>();
  /** How long the page stays — the time the pie takes to fill. */
  readonly seconds = input.required<number>();
  /** Seconds of the page already gone when it is drawn. */
  readonly elapsed = input(0);
}
